import { FC, useState, useMemo } from "react";
import { Tooltip } from "@mui/material";
import UpdateIcon from "@mui/icons-material/Update";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";

interface Props {
  onHover: () => void;
  onClick: () => void;
  onMouseLeave: () => void;
}

const UpdateAvailableIcon: FC<Props> = ({ onHover, onClick, onMouseLeave }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onHover();
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    onMouseLeave();
  };

  const pulseSx = useMemo(
    () => ({
      fontSize: 28,
      color: "inherit",
      animation: isHovered
        ? "pulseHover 1s infinite"
        : "pulseWindowed 30s infinite",
      "@keyframes pulseHover": {
        "0%": { opacity: 1 },
        "50%": { opacity: 0.6 },
        "100%": { opacity: 1 },
      },
      "@keyframes pulseWindowed": {
        "0%": { opacity: 1 },
        "1.67%": { opacity: 0.6 }, "3.33%": { opacity: 1 },
        "5%": { opacity: 0.6 }, "6.67%": { opacity: 1 },
        "8.33%": { opacity: 0.6 }, "10%": { opacity: 1 },
        "11.67%": { opacity: 0.6 }, "13.33%": { opacity: 1 },
        "15%": { opacity: 0.6 }, "16.67%": { opacity: 1 },
        "18.33%": { opacity: 0.6 }, "20%": { opacity: 1 },
        "21.67%": { opacity: 0.6 }, "23.33%": { opacity: 1 },
        "25%": { opacity: 0.6 }, "26.67%": { opacity: 1 },
        "28.33%": { opacity: 0.6 }, "30%": { opacity: 1 },
        "33%": { opacity: 1 },
        "100%": { opacity: 1 },
      },
      willChange: "opacity",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }),
    [isHovered]
  );

  const spinStyle = useMemo<React.CSSProperties>(
    () => ({
      display: "inline-flex",
      animation: isHovered ? "none" : "spinBurst 60s ease-in-out infinite",
      transformOrigin: "50% 50%",
    }),
    [isHovered]
  );

  return (
    <>
      <style>
        {`
          @keyframes spinBurst {
            0%   { transform: rotate(0deg); }
            25%  { transform: rotate(1080deg); } /* 3 spins in first 15s */
            100% { transform: rotate(1080deg); } /* hold for 45s */
          }
        `}
      </style>

      <Tooltip title={t("update_available")} arrow>
        <GenericButton
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          variant="icon"
          color="white"
          aria-label={t("update_available")}
          style={{
            color: "white",
            opacity: 1,
            padding: 0,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "42px",
            height: "42px",
            borderRadius: "50%",
            verticalAlign: "middle",
          }}
        >
          <span style={spinStyle}>
            <UpdateIcon sx={pulseSx} />
          </span>
        </GenericButton>
      </Tooltip>
    </>
  );
};

export default UpdateAvailableIcon;
